import { NetworkStatus, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { getDateRangeForView } from "@/domains/appointment/appointments/utils/get-date-range-for-view";
import type { AppointmentListFragment } from "@/gql/graphql";
import { APPOINTMENTS_QUERY } from "@/graphql/appointments";
import {
  APPOINTMENT_SORT_CONFIG,
  CACHE_POLICIES,
  POLLING_INTERVALS,
  QUERY_LIMITS,
} from "@/lib/config/query-config";
import { logError, StandardError, transformApolloError } from "@/lib/error/error-utils";
import { useSalonStore } from "@/lib/store/salon";

interface UseAppointmentsQueryProps {
  currentDate: Date;
  viewType: "day" | "3-day" | "week" | "month" | "worker";
  salonId?: string;
}

interface UseAppointmentsQueryReturn {
  data: AppointmentListFragment[];
  loading: boolean;
  error: StandardError | null;
  retry: () => void;
  refetch: () => Promise<any>;
}

/**
 * Query hook for fetching appointments with standardized error handling
 */
export const useAppointmentsQuery = ({
  currentDate,
  viewType,
  salonId,
}: UseAppointmentsQueryProps): UseAppointmentsQueryReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const effectiveSalonId = salonId || currentSalon?.id;

  // Get date range for the current view
  const { startDate, endDate } = useMemo(() => {
    // Convert JavaScript Date to Luxon DateTime for getDateRangeForView
    const luxonDate = DateTime.fromJSDate(currentDate);
    return getDateRangeForView(luxonDate, viewType);
  }, [currentDate, viewType]);

  // Query appointments for the date range
  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    error: apolloError,
    refetch: apolloRefetch,
    networkStatus,
  } = useQuery(APPOINTMENTS_QUERY, {
    variables: {
      salonId: effectiveSalonId || "",
      filters: {
        startDate: startDate.toISO(),
        endDate: endDate.toISO(),
        first: QUERY_LIMITS.APPOINTMENTS_DEFAULT,
        ...APPOINTMENT_SORT_CONFIG.DEFAULT,
      },
    },
    skip: !effectiveSalonId, // Skip query if no salon is selected
    pollInterval: POLLING_INTERVALS.APPOINTMENTS,
    ...CACHE_POLICIES.APPOINTMENTS,
    returnPartialData: true,
  });

  // Transform appointments data from cursor-based response
  const data = useMemo(() => {
    if (!appointmentsData?.appointments?.edges) {
      return [];
    }

    return appointmentsData.appointments.edges.map(
      (edge: any) => edge.node as AppointmentListFragment
    );
  }, [appointmentsData]);

  // Transform Apollo error to standardized error
  const error = useMemo(() => {
    if (!apolloError) return null;

    const standardError = transformApolloError(apolloError);
    logError(standardError, "useAppointmentsQuery");
    return standardError;
  }, [apolloError]);

  // Retry function with error handling
  const retry = useCallback(() => {
    apolloRefetch().catch((retryError) => {
      const standardError = transformApolloError(retryError);
      logError(standardError, "useAppointmentsQuery:retry");
    });
  }, [apolloRefetch]);

  // Refetch function (returns promise for advanced use cases)
  const refetch = useCallback(() => {
    return apolloRefetch();
  }, [apolloRefetch]);

  // Show loading when:
  // 1. Initial load (no data yet)
  // 2. Refetching (networkStatus === NetworkStatus.refetch) - e.g., when switching dates
  const isLoading = useMemo(() => {
    const isRefetching = networkStatus === NetworkStatus.refetch;
    const isInitialLoad = appointmentsLoading && data.length === 0;
    return isRefetching || isInitialLoad;
  }, [appointmentsLoading, networkStatus, data.length]);

  return {
    data,
    loading: isLoading,
    error,
    retry,
    refetch,
  };
};
